import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Icons } from "./ui/icons";
import DataTable, { TableRowData } from "./data-table";
import FormDrawer from "./form-drawer";
import SearchInput from "./search-input";
import SettingsSectionLoading from "./settings-section-loading";
import { RefreshToolbarButton } from "./refresh-toolbar-button";
import { bankDetailFormFields } from "../lib/form-fields";
import { bankDetailsTableColumns } from "../lib/table-columns";
import {
  BankDetail,
  CreateBankDetailPayload,
  createBankDetail,
  getBankDetails,
  updateBankDetail,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

function bankDetailToFormValues(bd: BankDetail) {
  return {
    bankName: bd.bankName,
    accountName: bd.accountName,
    accountNumber: bd.accountNumber,
    swiftCode: bd.swiftCode ?? "",
    branchName: bd.branchName ?? "",
  };
}

const BankDetailsSection: React.FC = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formValues, setFormValues] = useState<{ [key: string]: unknown } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const bankDetailsQuery = useQuery({
    queryKey: ["bank-details", { page: currentPage, limit: pageSize, search: searchTerm }],
    queryFn: async () => getBankDetails(currentPage, pageSize, searchTerm),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!bankDetailsQuery.error) return;
    toast.error(getBackendErrorMessage(bankDetailsQuery.error, "Failed to fetch bank details"));
  }, [bankDetailsQuery.error]);

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const tableData: TableRowData[] = useMemo(() => {
    return (bankDetailsQuery.data?.data ?? []).map((bd) => ({
      id: bd.id,
      bankName: bd.bankName,
      accountName: bd.accountName,
      accountNumber: bd.accountNumber,
      swiftCode: bd.swiftCode ?? "—",
      branchName: bd.branchName ?? "—",
      raw_data: bd,
    }));
  }, [bankDetailsQuery.data]);

  const totalItems = bankDetailsQuery.data?.meta.total ?? 0;
  const totalPages = bankDetailsQuery.data?.meta.totalPages ?? 1;
  const initialLoading = bankDetailsQuery.isLoading;
  const tableLoading = bankDetailsQuery.isFetching && !bankDetailsQuery.isLoading;

  const openEdit = (row: TableRowData) => {
    const raw = (row as { raw_data?: BankDetail }).raw_data;
    if (!raw) return;
    setEditingId(raw.id);
    setFormValues(bankDetailToFormValues(raw));
  };

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: openEdit,
    },
    {
      label: "Edit",
      icon: Edit,
      onClick: openEdit,
    },
  ];

  const buildPayload = (): CreateBankDetailPayload | null => {
    if (!formValues) return null;
    const bankName = String(formValues.bankName ?? "").trim();
    const accountName = String(formValues.accountName ?? "").trim();
    const accountNumber = String(formValues.accountNumber ?? "").trim();
    if (!bankName || !accountName || !accountNumber) {
      toast.error("Bank name, account name, and account number are required.");
      return null;
    }
    const swiftCode = String(formValues.swiftCode ?? "").trim();
    const branchName = String(formValues.branchName ?? "").trim();
    return {
      bankName,
      accountName,
      accountNumber,
      ...(swiftCode ? { swiftCode } : {}),
      ...(branchName ? { branchName } : {}),
    };
  };

  const handleFormSubmit = async () => {
    const payload = buildPayload();
    if (!payload) return;

    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateBankDetail(editingId, payload);
        toast.success("Bank details updated");
      } else {
        await createBankDetail(payload);
        toast.success("Bank details created");
      }
      setFormValues(null);
      setEditingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bank-details"] }),
      ]);
    } catch (error) {
      toast.error(
        getBackendErrorMessage(
          error,
          editingId ? "Failed to update bank details" : "Failed to create bank details",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (initialLoading) {
    return <SettingsSectionLoading />;
  }

  return (
    <>
      <FormDrawer
        title={editingId ? "Edit Bank Details" : "Add Bank Details"}
        buttonText={editingId ? "Update" : "Add"}
        formValues={formValues}
        setFormValues={setFormValues}
        fields={bankDetailFormFields}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormValues(null);
          setEditingId(null);
        }}
      />
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Manage payment instructions used on quotations and invoices. Records cannot be deleted;
          unlink from a document to stop using them.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={setSearchInput}
            placeholder="Search bank details..."
          />
          <RefreshToolbarButton
            className="ml-auto"
            isRefreshing={bankDetailsQuery.isFetching}
            onRefresh={() =>
              queryClient.invalidateQueries({ queryKey: ["bank-details"] })
            }
          />
          <Button
            variant="default"
            className="h-9"
            onClick={() => {
              setEditingId(null);
              setFormValues({});
            }}
          >
            <Icons.Plus className="h-4 w-4 mr-2" />
            Add bank details
          </Button>
        </div>

        <p className="text-sm text-gray-500">
          {totalItems.toLocaleString()} record{totalItems === 1 ? "" : "s"}
        </p>

        <DataTable
          columns={bankDetailsTableColumns}
          data={tableData}
          actions={actions}
          loading={tableLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </div>
    </>
  );
};

export default BankDetailsSection;
